import React, { ReactNode, useEffect, useState } from 'react';
import {
  DecisionTreeAnswer,
  DecisionTreeNextQuestion,
  DecisionTreeQuestion
} from '../../../../@types';
import { API_ASSETS } from '../../../../services';
import { hidePreviousQuestions, hideUnansweredQuestions } from '../utils';

type AppType = 'remodel' | 'divorce';

interface DecisionTreeContextProps {
  appType: AppType;
  isLoading: boolean;
  questions: DecisionTreeQuestion[];
  currentQuestion: DecisionTreeQuestion | null;
  setAnswer: (question: DecisionTreeQuestion, answer: DecisionTreeAnswer) => void;
  resetToQuestion: (question: DecisionTreeQuestion) => void;
  setIsLoading: (state: boolean) => void;
}

export const DecisionTreeContext = React.createContext<DecisionTreeContextProps>({
  appType: 'remodel',
  isLoading: false,
  questions: [],
  currentQuestion: null,
  setAnswer: (question: DecisionTreeQuestion, answer: DecisionTreeAnswer) => {
    console.log({ question, answer });
    throw new Error('setAnswer function not implemented');
  },
  resetToQuestion: (question: DecisionTreeQuestion) => {
    console.log({ question });
    throw new Error('resetToQuestion function not implemented');
  },
  setIsLoading: (state: boolean) => {
    console.log({ state });
    throw new Error('setIsLoading function not implemented');
  }
});

export const DecisionTreeProvider: React.FC<{
  children: ReactNode;
  type: string | null;
}> = ({ children, type }) => {
  const [questions, setQuestions] = useState<DecisionTreeQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<DecisionTreeQuestion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [appType] = useState<AppType>(type ? (type as AppType) : 'remodel');
  // get questions from json
  useEffect(() => {
    setIsLoading(true);
    API_ASSETS.get<DecisionTreeQuestion[]>(
      `decision-tree/${appType}/json/questions-${appType}.json`
    ).then((res) => {
      //console.log(res);
      setQuestions(handleQuestions(res));
      setCurrentQuestion(res[0]);
      setIsLoading(false);
    });
  }, [setQuestions, setCurrentQuestion]);

  function setAnswer(question: DecisionTreeQuestion, answer: DecisionTreeAnswer) {
    question.answer = answer;

    setQuestions(handleQuestions(questions));

    if (answer.nextQuestion.conditionals) {
      // foreach conditional
      answer.nextQuestion.conditionals.forEach((conditional) => {
        // check if question has answer
        const questionToCheckAnswer = questions.find((q) => q.id === conditional.questionId);
        if (
          questionToCheckAnswer?.answer?.id === conditional.hasAnswerId &&
          conditional.nextQuestion
        ) {
          handleNextQuestion(question, conditional.nextQuestion);
        }
      });
    } else {
      handleNextQuestion(question, answer.nextQuestion);
    }
  }

  function handleQuestions(questions: DecisionTreeQuestion[]) {
    // check if any questions have a conditional
    questions.forEach((question) => {
      if (question.conditionals) {
        question.conditionals.forEach((conditional) => {
          const questionToCheckAnswer = questions.find((q) => q.id === conditional.questionId);
          if (questionToCheckAnswer?.answer?.id === conditional.hasAnswerId) {
            question.question = conditional.question || question.question;
          }
        });
      }
    });

    return questions;
  }

  function handleNextQuestion(
    question: DecisionTreeQuestion,
    next: Omit<DecisionTreeNextQuestion, 'conditionals'>
  ) {
    let nextQuestion: DecisionTreeQuestion | null = null;
    // goToNextQuestion
    if (next.goToNextQuestion) {
      const questionIndex = questions.findIndex((q) => q.id === question.id);
      nextQuestion = questions.length >= questionIndex + 1 ? questions[questionIndex + 1] : null;
    }
    // go to NextQuestion with ID
    if (next.nextQuestionId) {
      nextQuestion = questions.find((q) => q.id === next.nextQuestionId) || null;
    }
    // go to result
    if (next.goToResult) {
      nextQuestion = null;
    }

    setCurrentQuestion(nextQuestion);
    if (nextQuestion) {
      setQuestions(hidePreviousQuestions(questions, nextQuestion?.id));
    } else {
      setQuestions(hideUnansweredQuestions(questions));
    }
  }

  function resetToQuestion(question: DecisionTreeQuestion) {
    // clear all question answers after this question
    const nextQuestionIndex = questions.findIndex((q) => q.id === question.id);
    questions.forEach((q, index) => {
      if (index >= nextQuestionIndex) {
        q.answer = undefined;
        q.hide = false;
      }
    });

    setCurrentQuestion(question);
  }

  return (
    <DecisionTreeContext.Provider
      value={{
        isLoading,
        questions,
        appType,
        currentQuestion,
        setIsLoading,
        resetToQuestion,
        setAnswer
      }}>
      {children}
    </DecisionTreeContext.Provider>
  );
};
